import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { Lang } from "../i18n";
import { getSeoDocument, siteOrigin, type SeoDocument, type SeoPage } from "../seoConfig";

export type { SeoPage } from "../seoConfig";

function setHeadElement(selector: string, tagName: "meta" | "link", attrs: Record<string, string>) {
  let element = document.head.querySelector(selector) as HTMLElement | null;
  if (!element) {
    element = document.createElement(tagName);
    document.head.appendChild(element);
  }
  Object.entries(attrs).forEach(([key, value]) => element?.setAttribute(key, value));
}

function removeHeadElements(selector: string) {
  document.head.querySelectorAll(selector).forEach((element) => element.remove());
}

function setStructuredData(value: Record<string, unknown> | null) {
  removeHeadElements('script[type="application/ld+json"][data-clawease-seo]');
  if (!value) return;

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.dataset.claweaseSeo = "true";
  script.textContent = JSON.stringify(value);
  document.head.appendChild(script);
}

export function Seo({ lang, page = "home", document: overrideDocument, documentLanguage }: { lang: Lang; page?: SeoPage; document?: SeoDocument; documentLanguage?: string }) {
  const location = useLocation();

  useEffect(() => {
    const seo = overrideDocument || getSeoDocument(page, lang);
    document.documentElement.lang = documentLanguage || (lang === "zh" ? "zh-CN" : "en");
    document.title = seo.title;

    setHeadElement('meta[name="description"]', "meta", { name: "description", content: seo.description });
    setHeadElement('meta[name="keywords"]', "meta", { name: "keywords", content: seo.keywords.join(", ") });
    setHeadElement('meta[name="robots"]', "meta", { name: "robots", content: seo.robots });

    if (seo.canonicalUrl) {
      setHeadElement('link[rel="canonical"]', "link", { rel: "canonical", href: seo.canonicalUrl });
    } else {
      removeHeadElements('link[rel="canonical"]');
    }

    setHeadElement('meta[property="og:type"]', "meta", { property: "og:type", content: "website" });
    setHeadElement('meta[property="og:site_name"]', "meta", { property: "og:site_name", content: "ClawEase" });
    setHeadElement('meta[property="og:title"]', "meta", { property: "og:title", content: seo.title });
    setHeadElement('meta[property="og:description"]', "meta", { property: "og:description", content: seo.description });
    setHeadElement('meta[property="og:url"]', "meta", { property: "og:url", content: seo.canonicalUrl || window.location.href });
    setHeadElement('meta[property="og:image"]', "meta", { property: "og:image", content: `${siteOrigin}/favicon.svg` });
    setHeadElement('meta[name="twitter:card"]', "meta", { name: "twitter:card", content: "summary" });
    setHeadElement('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: seo.title });
    setHeadElement('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: seo.description });

    removeHeadElements('link[rel="alternate"][hreflang]');
    seo.alternates.forEach(({ hreflang, href }) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = hreflang;
      link.href = href;
      document.head.appendChild(link);
    });
    setStructuredData(seo.structuredData);
  }, [lang, location.pathname, overrideDocument, page, documentLanguage]);

  return null;
}
